import { defineAction } from '~/prix'
import { z } from 'zod'
import insightSendEmailAction from './insightSendEmail.action.json'
import { ALERT_PRESET_BASES, ALERT_PRESET_OUTCOMES, toAlertType } from '~/packages/variable/spaceTime.data'

const normalizeStringArrayInput = (value: unknown) => {
  if (value == null) {
    return undefined
  }

  const values = Array.isArray(value) ? value : [value]

  const normalizedValues = values
    .flatMap(currentValue => String(currentValue).split(','))
    .map(currentValue => currentValue.trim())
    .filter(Boolean)

  return normalizedValues.length > 0 ? normalizedValues : undefined
}

const stringArrayInputZod = z.preprocess(
  normalizeStringArrayInput,
  z.array(z.string().trim().min(1)).optional(),
)

const uuidArrayInputZod = z.preprocess(
  normalizeStringArrayInput,
  z.array(z.string().uuid()).optional(),
)

const emailArrayInputZod = z.preprocess(
  normalizeStringArrayInput,
  z.array(z.string().trim().email()).optional(),
)

const alertPresetOptions = [
  'with-alert',
  'without-alert',
  ...ALERT_PRESET_BASES.flatMap(base => ALERT_PRESET_OUTCOMES.map(outcome => toAlertType(outcome, base))),
] as [string, ...string[]]

const insightSendEmailDefinition = defineAction({
  ...insightSendEmailAction,
  input: z
    .object({
      emails: emailArrayInputZod,
      insightIds: uuidArrayInputZod,
      delimitation: z.string().trim().optional(),
      variableKeys: stringArrayInputZod,
      variablePresetKeys: stringArrayInputZod,
      alertType: stringArrayInputZod,
      alertPreset: z.enum(alertPresetOptions).optional(),
      limit: z.number().int().positive().optional(),
      offset: z.number().int().min(0).optional(),
      fallbackToGlobal: z.boolean().optional(),
      title: z.string().trim().optional(),
      message: z.string().trim().optional(),
      recipientName: z.string().trim().optional(),
      previewOnly: z.boolean().optional(),
    })
    .superRefine((value, ctx) => {
      if (!value.previewOnly && (!value.emails || value.emails.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['emails'],
          message: 'emails é obrigatório quando previewOnly não está habilitado',
        })
      }
    }),
  output: z.object({
    warnings: z.array(
      z.object({
        message: z.string(),
      }),
    ),
    success: z.object({
      success: z.boolean(),
      id: z.string(),
    }),
    source: z.enum(['filtered', 'global', 'selected']).optional(),
    preview: z
      .object({
        html: z.string(),
        title: z.string(),
        recipients: z.array(z.string()),
        insightsCount: z.number(),
      })
      .optional(),
  }),
})

export default insightSendEmailDefinition
